import api from '../../../services/api';
import {
  asArray,
  asNumber,
  asRecord,
  asString,
  isRecord,
} from '../../../dto/common';

// Single home for the "Księga partii" (Etap 4) server contract:
// GET /api/reports/batch-ledger?from=YYYY-MM&to=YYYY-MM — one entry per
// received InventoryBatch, grouped by month of receipt, with every WZ release
// the batch fed hanging off it. Backend home: reportService.buildBatchLedger.
//
// The endpoint is gated on requireFinancialReports (ADMIN/finance only; never
// OPERATIONS), so the page that consumes this must be gated the same way.
//
// Money contract: every PLN figure is the stored PLN-canonical column and the
// per-month / grand summaries are computed server-side. This module NEVER sums
// money — the owner directive is that headline period figures come from the
// server, and re-adding them here would be a second, divergent home.

export interface BatchLedgerRelease {
  wzDocumentId: string | null;
  wzNumber: string | null;
  releaseDate: string | null;
  customerName: string | null;
  salesInvoiceNumber: string | null;
  quantityConsumed: number;
  quantityConsumedPcs?: number | null;
  quantityConsumedM2?: number | null;
  quantityConsumedM3?: number | null;
  commercialQuantityConsumed: number | null;
  cogsPLN: number | null;
}

export interface BatchLedgerDimensions {
  width: number | null;
  height: number | null;
  length: number | null;
  thickness: number | null;
}

export interface BatchLedgerBatch {
  batchId: string;
  batchNumber: string;
  receiptDate: string | null;
  pzDocumentId: string | null;
  pzNumber: string | null;
  supplierName: string | null;
  supplierInvoiceNumber: string | null;
  productName: string | null;
  productVariantId: string | null;
  dimensions: BatchLedgerDimensions | null;
  physicalQuantity: number;
  physicalUnit: string | null;
  quantityReceivedPcs?: number | null;
  quantityReceivedM2?: number | null;
  quantityReceivedM3?: number | null;
  commercialQuantity: number | null;
  commercialUnit: string | null;
  unitPrice: number | null;
  /**
   * Unit the unitPrice is quoted PER: the commercial purchaseUnit when the
   * batch has a genuine trusted commercial pair, otherwise the physical unit.
   * Server-owned — a dims-DERIVED m²/m³ figure still carries a per-piece
   * price, so this must not be inferred from commercialUnit alone.
   */
  unitPriceUnit: string | null;
  currency: string | null;
  valueOriginal: number | null;
  valuePLN: number | null;
  /** Capitalized freight/landed cost already included in valuePLN; null for non-stock batches. */
  capitalizedExtraPLN: number | null;
  quantityRemaining: number;
  quantityRemainingPcs?: number | null;
  quantityRemainingM2?: number | null;
  quantityRemainingM3?: number | null;
  commercialQuantityRemaining?: number | null;
  remainingValuePLN: number | null;
  releases: BatchLedgerRelease[];
}

export interface BatchLedgerSummary {
  batchCount: number;
  totalValuePLN: number;
  remainingValuePLN: number;
}

export interface BatchLedgerMonth {
  month: string;
  batches: BatchLedgerBatch[];
  summary: BatchLedgerSummary;
}

export interface BatchLedgerResponse {
  from: string;
  to: string;
  months: BatchLedgerMonth[];
  totals: BatchLedgerSummary;
}

export interface BatchLedgerExport {
  blob: Blob;
  filename: string;
}

const DEFAULT_EXPORT_FILENAME = 'ksiega-magazynowa.xlsx';

function filenameFromContentDisposition(value: unknown): string {
  if (typeof value !== 'string') return DEFAULT_EXPORT_FILENAME;

  const utf8Match = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1].replace(/^"|"$/g, ''));
    } catch {
      return DEFAULT_EXPORT_FILENAME;
    }
  }

  const plainMatch = value.match(/filename="?([^";]+)"?/i);
  return plainMatch?.[1]?.trim() || DEFAULT_EXPORT_FILENAME;
}

/**
 * Money and derived quantities stay `null` when the server has nothing to
 * report, so the ledger can render "—" instead of a fabricated 0,00 zł.
 * See frontend/src/dto/AGENTS.md — absence is preserved as absence.
 */
function asNullableNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function asNullableString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function normalizeDimensions(raw: unknown): BatchLedgerDimensions | null {
  const record = asRecord(raw);
  if (!record) {
    return null;
  }

  const dimensions: BatchLedgerDimensions = {
    width: asNullableNumber(record.width),
    height: asNullableNumber(record.height),
    length: asNullableNumber(record.length),
    thickness: asNullableNumber(record.thickness),
  };

  const hasAny = Object.values(dimensions).some((value) => value !== null);
  return hasAny ? dimensions : null;
}

function normalizeRelease(raw: unknown): BatchLedgerRelease {
  const record = asRecord(raw) ?? {};

  return {
    wzDocumentId: asNullableString(record.wzDocumentId),
    wzNumber: asNullableString(record.wzNumber),
    releaseDate: asNullableString(record.releaseDate),
    customerName: asNullableString(record.customerName),
    salesInvoiceNumber: asNullableString(record.salesInvoiceNumber),
    quantityConsumed: asNumber(record.quantityConsumed),
    quantityConsumedPcs: asNullableNumber(record.quantityConsumedPcs),
    quantityConsumedM2: asNullableNumber(record.quantityConsumedM2),
    quantityConsumedM3: asNullableNumber(record.quantityConsumedM3),
    commercialQuantityConsumed: asNullableNumber(record.commercialQuantityConsumed),
    cogsPLN: asNullableNumber(record.cogsPLN),
  };
}

function normalizeBatch(raw: unknown): BatchLedgerBatch {
  const record = asRecord(raw) ?? {};

  return {
    batchId: asString(record.batchId),
    batchNumber: asString(record.batchNumber),
    receiptDate: asNullableString(record.receiptDate),
    pzDocumentId: asNullableString(record.pzDocumentId),
    pzNumber: asNullableString(record.pzNumber),
    supplierName: asNullableString(record.supplierName),
    supplierInvoiceNumber: asNullableString(record.supplierInvoiceNumber),
    productName: asNullableString(record.productName),
    productVariantId: asNullableString(record.productVariantId),
    dimensions: normalizeDimensions(record.dimensions),
    physicalQuantity: asNumber(record.physicalQuantity),
    physicalUnit: asNullableString(record.physicalUnit),
    quantityReceivedPcs: asNullableNumber(record.quantityReceivedPcs),
    quantityReceivedM2: asNullableNumber(record.quantityReceivedM2),
    quantityReceivedM3: asNullableNumber(record.quantityReceivedM3),
    commercialQuantity: asNullableNumber(record.commercialQuantity),
    commercialUnit: asNullableString(record.commercialUnit),
    unitPrice: asNullableNumber(record.unitPrice),
    unitPriceUnit: asNullableString(record.unitPriceUnit),
    currency: asNullableString(record.currency),
    valueOriginal: asNullableNumber(record.valueOriginal),
    valuePLN: asNullableNumber(record.valuePLN),
    capitalizedExtraPLN: asNullableNumber(record.capitalizedExtraPLN),
    quantityRemaining: asNumber(record.quantityRemaining),
    quantityRemainingPcs: asNullableNumber(record.quantityRemainingPcs),
    quantityRemainingM2: asNullableNumber(record.quantityRemainingM2),
    quantityRemainingM3: asNullableNumber(record.quantityRemainingM3),
    commercialQuantityRemaining: asNullableNumber(record.commercialQuantityRemaining),
    remainingValuePLN: asNullableNumber(record.remainingValuePLN),
    releases: asArray(record.releases, normalizeRelease),
  };
}

function normalizeSummary(raw: unknown): BatchLedgerSummary {
  const record = asRecord(raw) ?? {};

  return {
    batchCount: asNumber(record.batchCount),
    totalValuePLN: asNumber(record.totalValuePLN),
    remainingValuePLN: asNumber(record.remainingValuePLN),
  };
}

function normalizeMonth(raw: unknown): BatchLedgerMonth {
  const record = asRecord(raw) ?? {};

  return {
    month: asString(record.month),
    batches: asArray(record.batches, normalizeBatch),
    summary: normalizeSummary(record.summary),
  };
}

export function normalizeBatchLedgerResponse(raw: unknown): BatchLedgerResponse {
  const record = asRecord(raw) ?? {};

  return {
    from: asString(record.from),
    to: asString(record.to),
    months: asArray(record.months, normalizeMonth),
    totals: normalizeSummary(record.totals),
  };
}

export async function fetchBatchLedger(
  from?: string,
  to?: string,
): Promise<BatchLedgerResponse> {
  const params: Record<string, string> = {};
  if (from) params.from = from;
  if (to) params.to = to;

  const response = await api.get('/reports/batch-ledger', { params });
  return normalizeBatchLedgerResponse(isRecord(response?.data) ? response.data : null);
}

export async function exportBatchLedger(
  from?: string,
  to?: string,
): Promise<BatchLedgerExport> {
  const params: Record<string, string> = {};
  if (from) params.from = from;
  if (to) params.to = to;

  const response = await api.get('/reports/batch-ledger/export', {
    params,
    responseType: 'blob',
  });

  return {
    blob: response.data,
    filename: filenameFromContentDisposition(response.headers?.['content-disposition']),
  };
}
